import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import {
  FieldShell,
  NumberInput,
  errorMessage,
  type AdjustmentNoteCoreFieldsProps,
} from './adjustment-note-fields';
import { ADJUSTMENT_STATUS_OPTIONS, NOTE_REASONS } from './adjustment-note-schema';
import type { AdjustmentNoteSubmitValues } from './adjustment-note-schema';

// Simple supplier-invoice layout shared by Credit notes (both sides) and
// buy-side Debit notes (Launchpad parity): Invoice number → Company name →
// Currency → Reason → Total → Invoice date → Due date → Status → Notes. VAT,
// settlement method and adjustment lines live on the sell-side Debit form only.
export function SimpleNoteCoreFields(props: AdjustmentNoteCoreFieldsProps) {
  return (
    <div className="flex flex-col gap-4">
      <SimpleNoteIdentityFields {...props} />
      <SimpleNoteScheduleFields {...props} />
    </div>
  );
}

function SimpleNoteIdentityFields(props: AdjustmentNoteCoreFieldsProps) {
  const err = (key: keyof AdjustmentNoteSubmitValues): string | undefined =>
    errorMessage(props.errors, key);
  return (
    <>
      <FieldShell id="invoice_number" label="Invoice Number" error={err('invoice_number')}>
        <Input
          id="invoice_number"
          type="text"
          autoComplete="off"
          placeholder={`${props.noteType.toUpperCase()}_NOTE-001`}
          disabled={props.disabled}
          {...props.register('invoice_number')}
        />
      </FieldShell>
      <FieldShell id="company_name" label="Company Name" error={err('company_name')}>
        <Input
          id="company_name"
          type="text"
          autoComplete="off"
          disabled={props.disabled}
          {...props.register('company_name')}
        />
      </FieldShell>
      <FieldShell id="currency" label="Currency" error={err('currency')}>
        <Select
          id="currency"
          disabled={props.disabled || props.currencyLocked}
          {...props.register('currency')}
        >
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
        </Select>
      </FieldShell>
      <FieldShell id="note_reason" label="Reason" error={err('note_reason')}>
        <Select id="note_reason" disabled={props.disabled} {...props.register('note_reason')}>
          <option value="">Select reason…</option>
          {NOTE_REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
      </FieldShell>
      <NumberInput id="total_amount" label="Total Amount" props={props} />
    </>
  );
}

function SimpleNoteScheduleFields(props: AdjustmentNoteCoreFieldsProps) {
  const err = (key: keyof AdjustmentNoteSubmitValues): string | undefined =>
    errorMessage(props.errors, key);
  return (
    <>
      <FieldShell id="issue_date" label="Invoice Date (Supplier)" error={err('issue_date')}>
        <Input
          id="issue_date"
          type="date"
          disabled={props.disabled}
          {...props.register('issue_date')}
        />
      </FieldShell>
      <FieldShell id="original_due_date" label="Original Due Date" error={err('original_due_date')}>
        <Input
          id="original_due_date"
          type="date"
          disabled={props.disabled}
          {...props.register('original_due_date')}
        />
      </FieldShell>
      <FieldShell id="status" label="Status" error={err('status')}>
        <Select id="status" disabled {...props.register('status')}>
          {ADJUSTMENT_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <p className="text-muted-foreground text-xs">
          Status moves through invoice actions: Issue sets Issued (and assigns the number),
          recording payments sets Paid or Partially Paid, Cancel sets Cancelled.
        </p>
      </FieldShell>
      <FieldShell id="remarks" label="Notes" error={err('remarks')}>
        <Textarea
          id="remarks"
          placeholder="Additional notes…"
          disabled={props.disabled}
          {...props.register('remarks')}
        />
      </FieldShell>
    </>
  );
}
